<template>
  <v-container v-if="fragment"
               fluid>
    <page-title icon="fas fa-fw fa-cabinet-filing">
      Edit Space Fragment
    </page-title>

    <v-form @submit.prevent="submit">
      <v-card class="mb-4">
        <v-card-text>
          <v-text-field v-model="form.name"
                        label="Name"
                        hint="This is only visible to administrators. It will not be visible to customers."
                        persistent-hint
                        outlined
                        dense
                        required
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('name')"
                        class="mb-4">
          </v-text-field>

          <v-autocomplete v-model="form.locale"
                          label="Language"
                          outlined
                          dense
                          :items="locales"
                          item-value="id"
                          item-text="name"
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('locale')">
          </v-autocomplete>

          <v-switch v-model="form.is_active"
                    :label="form.is_active ? 'Active' : 'Inactive'"
                    class="pt-0 mt-0">
          </v-switch>

          <v-autocomplete v-model="form.spaces"
                          label="Spaces"
                          outlined
                          dense
                          :items="spaces"
                          item-value="id"
                          :item-text="spaceInfo"
                          multiple
                          chips
                          deletable-chips
                          small-chips
                          clearable
                          :error-messages="form.errors.get('spaces')"
                          hint="Associate this fragment with these spaces."
                          persistent-hint
                          class="mb-4">
          </v-autocomplete>

          <v-select v-model="form.collapsed_height"
                    label="Collapsed Height"
                    outlined
                    dense
                    hint="This only applies when the fragment's position is 'Footer'."
                    persistent-hint
                    :items="collapsedHeights"
                    item-value="id"
                    item-text="text"
                    class="mb-4">
          </v-select>

          <v-text-field v-model="form.dialog_title"
                        label="Title"
                        outlined
                        dense
                        hint="A customer only sees the title when the fragment's position is 'Footer'."
                        persistent-hint
                        required
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('title')"
                        class="mb-4">
          </v-text-field>

          <tinymce-editor api-key="w7qx9l4xwuc114tjfvqngdobxbi48cjwt782qof3qt0ht031"
                          id="tm3"
                          :init="tinyConfig"
                          v-model="form.content">
          </tinymce-editor>

          <MicrosoftPasteAlert>
          </MicrosoftPasteAlert>

        </v-card-text>
        <v-card-actions>
          <v-btn color="primary"
                 type="submit">
            Update
          </v-btn>
          <v-btn :to="{name: 'admin.space_fragments.index'}">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click.prevent="deleteFragment"
                 color="error">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>
<script>
import {Form} from 'vform';
import tinymceService from '../../../../ml/services/tinymceService';
import spaceService from '../../services/spaceService';
import {formValidation} from '../../plugins';
import DOMPurify from 'dompurify';
import {mapGetters} from 'vuex';
import PageTitle from '../../components/PageTitle.vue';
import CodeSnippet from '../share_templates/components/CodeSnippet.vue';
import MicrosoftPasteAlert from '../../components/MicrosoftPasteAlert.vue';

export default {
  components: {MicrosoftPasteAlert, CodeSnippet, PageTitle},
  mixins: [formValidation],
  data() {
    return {
      error: null,
      form: new Form({
        name: null,
        locale: 'en', // MLD-5550
        dialog_title: null,
        is_active: false,
        collapsed_height: '200px',
        spaces: [],
      }),
      collapsedHeights: [
        {id: '100px', text: '100px'},
        {id: '200px', text: '200px (Recommended)'},
        {id: '300px', text: '300px'},
      ],
      fragment: null,
      spaces: [],
    }
  },
  computed: {
    ...mapGetters([
      'locales', // MLD-5550
    ]),
    fragmentID() {
      return parseInt(this.$route.params.fragmentID);
    },
    tinyConfig() {
      const config = {
        content_css: `/v2/css/tinymce_editor.css?v=${new Date().getTime()}`, // MLD-2694
        selector: '#tinyEditor',
        height: 300,
        //menubar: 'view insert format tools table help',
        menubar: 'mobilelocker edit view insert format tools table help', // @see https://www.tiny.cloud/docs/configure/editor-appearance/#menubar
        menu: { // @see https://www.tiny.cloud/docs/configure/editor-appearance/#menu
          //file: { title: 'File', items: 'newdocument restoredraft | preview | print ' },
          edit: {title: 'Edit', items: 'undo redo | cut copy paste | selectall | searchreplace'},
          view: {title: 'View', items: 'code | visualaid visualchars visualblocks | spellchecker | preview fullscreen'},
          insert: {
            title: 'Insert',
            items: 'image link media template codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor toc | insertdatetime'
          },
          format: {
            title: 'Format',
            items: 'bold italic underline strikethrough superscript subscript codeformat | formats blockformats fontformats fontsizes align lineheight | forecolor backcolor | removeformat'
          },
          tools: {title: 'Tools', items: 'spellchecker spellcheckerlanguage | code wordcount'},
          table: {title: 'Table', items: 'inserttable | cell row column | tableprops deletetable'},
          help: {title: 'Help', items: 'help'}
        },
        //quickbars_insert_toolbar: 'ml_quick_insert', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#exampledisablingthequickinsertcontexttoolbar
        quickbars_insert_toolbar: false,
        quickbars_selection_toolbar: false, //'bold italic | quicklink', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#quickbars_selection_toolbar
        toolbar: 'undo redo | bold italic underline superscript subscript | numlist bullist | link image | removeformat | fontselect formatselect fontsizeselect | alignleft aligncenter alignright alignjustify',
        contextmenu: false, //'context_menu_ml_insert_tag | context_menu_ml_lock | link', // @see https://www.tiny.cloud/docs/ui-components/contextmenu/
      }
      return tinymceService.getConfig(config);
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      spaceService.getSpaces()
        .then(spaces => {
          this.spaces = spaces;
        })
        .catch(() => {
        })

      return spaceService.getFragment(this.fragmentID)
        .then(fragment => {
          this.fragment = fragment;
          this.initForm();
        })
        .catch(() => {
          this.$toast.error('Fragment not found.');
          this.$router.replace({name: 'admin.space_fragments.index'});
        })
        .finally(() => {
          this.busy = false;
        })
    },
    initForm() {
      this.form.name = this.fragment.name;
      this.form.locale = this.fragment.locale; // MLD-5550
      this.form.dialog_title = this.fragment.dialog_title;
      this.form.is_active = this.fragment.is_active;
      this.form.collapsed_height = this.fragment.collapsed_height;
      this.form.content = this.fragment.content;
      this.form.spaces = this.fragment.spaces.map(s => s.id);
    },
    submit() {
      this.form.put(`/api/space-fragments/${this.fragment.id}`)
        .then(response => {
          this.$toast.success('Fragment updated.');
        })
        .catch(() => {
        })
    },
    deleteFragment() {
      if (confirm('Are you sure you want to delete this fragment?')) {
        this.form.delete(`/api/space-fragments/${this.fragment.id}`)
          .then(() => {
            this.$toast.success('Fragment deleted.');
            return this.$router.replace({name: 'admin.space_fragments.index'});
          })
          .catch(err => {
            if (err.response?.data?.message) {
              this.$toast.error(DOMPurify.sanitize(err.response?.data?.message));
            }
          })
      }
    },
    spaceInfo(space) { // MLD-5550
      return DOMPurify.sanitize(`${space.name} (${space.locale_name})`);
    }
  }
}
</script>
